import React, { useRef } from "react";
import { Link } from "@nextui-org/link";
import ScrollSpy from "react-ui-scrollspy";

import { button as buttonStyles } from "@nextui-org/theme";
import { Listbox, ListboxItem } from "@nextui-org/listbox";
import { Divider } from "@nextui-org/divider";
import { subtitle } from "@/components/primitives";

const policySideMenu = [
  {
    id: 1,
    url: "intro",
    title: "Introduction",
  },
  {
    id: 2,
    url: "information",
    title: "Information We Collect",
  },
  {
    id: 3,
    url: "rights",
    title: "Your Rights",
  },
  {
    id: 4,
    url: "changes",
    title: "Changes To Privacy Policy",
  },
  {
    id: 5,
    url: "contact",
    title: "Contact Us",
  },
];

const PolicySpyCom = () => {
  const parentScrollContainerRef = useRef(null);
  const onPress = (key: any) => {
    const target = window.document.getElementById(key);

    if (target) {
      target.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <div className="container lg:px-16 xl:px-16 md:px-16 px-2 pb-28">
      <div className="flex flex-row justify-center flex-wrap mt-[80px]">
        <div className="lg:w-1/4 xl:w-1/4 w-full self-start p-3 sm:p-2 relative mb-8 lg:sticky lg:top-10">
          <div className="relative">
            <Listbox
              aria-label="PolicyList"
                className="p-0 gap-4 divide-y divide-default-300/50 dark:divide-default-100/80 bg-content1 overflow-visible shadow-small rounded-medium"
                itemClasses={{
                  base: "px-3 py-4 first:rounded-t-medium last:rounded-b-medium rounded-none gap-3 h-12 data-[hover=true]:bg-default-100/80",
                }}
                items={policySideMenu}
                selectionMode="single"
                variant="flat"
                onAction={(key) => onPress(key)}>
            
              {(item) => <ListboxItem key={item.url}>{item.title}</ListboxItem>}
            </Listbox>
          </div>
        </div>
        <div className="lg:w-3/4 xl:w-3/4 w-full">
          <div className="ct-bg-white flex flex-col justify-center items-center lg:mx-8 my-0 rounded-md">
            <div className="ct-faint-bg ct-sm-margin rounded-md">
              <div ref={parentScrollContainerRef} className="p-4">
                <ScrollSpy scrollThrottle={100} useBoxMethod={false}>
                  <div className="py-6" id="intro">
                    <h1 className="text-3xl lg:text-6lg tracking-tight inline font-semibold">
                      Introduction
                    </h1>
                    <div className="mt-5 gap-4">
                      <p className="text-md lg:text-lg xl:text-lg text-default-600 font-medium block max-w-full">
                        CryptoTX (“we”, “us”, “our”) respects the privacy of our
                        users (“you”, “your”). This Privacy Policy explains how
                        we collect, use, disclose, and safeguard your
                        information when you visit our website and use our
                        services. We are committed to protecting your personal
                        information and your right to privacy.
                      </p>
                    </div>
                  </div>
                  <Divider className="my-8" />
                  <div className="py-6" id="information">
                    <h2 className="text-3xl lg:text-6lg tracking-tight inline font-semibold">
                      Information We Collect
                    </h2>
                    <div className="mt-5 gap-4">
                      <p className="text-md lg:text-lg xl:text-lg text-default-600 font-medium block max-w-full">
                        We collect information that identifies, relates to,
                        describes, or is capable of being associated with you.
                        This may include personal information such as your name,
                        address, email address, telephone number, bank account
                        details, and transaction history.
                      </p>
                      <h5 className={subtitle()}>How We Collect Information</h5>
                      <ul className="mt-3 mb-10">
                        <li className="lg:text-lg text-default-600 font-medium block">
                          <b>Directly from you:</b> When you register for an
                          account, perform transactions, or contact us for
                          support.
                        </li>
                        <li className="lg:text-lg text-default-600 font-medium block">
                          <b>Indirectly from you:</b> Through your actions on
                          our website, such as information collected by cookies
                          and other similar technologies.
                        </li>
                      </ul>

                      <h5 className={subtitle()}>Use of Your Information</h5>
                      <ul className="mt-3 mb-10">
                        <li className="lg:text-lg text-default-600 font-medium block">
                          We may use the information we collect for various
                          purposes, including:
                        </li>
                        <li className="lg:text-lg text-default-600 font-medium block">
                          To facilitate, manage, and provide our services.
                        </li>
                        <li className="lg:text-lg text-default-600 font-medium block">
                          To comply with legal and regulatory requirements.
                        </li>
                        <li className="lg:text-lg text-default-600 font-medium block">
                          To improve our website and services.
                        </li>
                        <li className="lg:text-lg text-default-600 font-medium block">
                          To communicate with you, including sending
                          service-related notices.
                        </li>
                      </ul>

                      <h5 className={subtitle()}>
                        {" "}
                        Disclosure of Your Information
                      </h5>
                      <ul className="mt-3 mb-10">
                        <li className="lg:text-lg text-default-600 font-medium block">
                          We may share your information with third parties in
                          the following situations:
                        </li>
                        <li className="lg:text-lg text-default-600 font-medium block">
                          With service providers who perform services for us.
                        </li>
                        <li className="lg:text-lg text-default-600 font-medium block">
                          To comply with legal obligations, such as responding
                          to a subpoena or regulatory request.
                        </li>
                        <li className="lg:text-lg text-default-600 font-medium block">
                          To protect the rights, property, or safety of
                          CryptoTx, our users, or others.
                        </li>
                      </ul>

                      <h5 className={subtitle()}>
                        Security of Your Information
                      </h5>
                      <p className="lg:text-lg text-default-600 font-medium block">
                        We implement and maintain reasonable security practices
                        to protect against unauthorized access, alteration,
                        disclosure, or destruction of personal information.
                      </p>
                    </div>
                  </div>
                  <Divider className="my-8" />

                  <div className="py-6" id="rights">
                    <h3 className="text-3xl lg:text-6lg tracking-tight inline font-semibold">
                      Your Rights
                    </h3>
                    <p className="mt-5 gap-4">
                      You have certain rights regarding the personal information
                      we hold about you. These may include the right to access,
                      correct, or delete the information, as well as the right
                      to restrict or object to our use of your information.
                    </p>
                  </div>
                  <Divider className="my-8" />

                  <div className="py-6" id="changes">
                    <h4 className="text-3xl lg:text-6lg tracking-tight inline font-semibold">
                      {" "}
                      Changes to This Privacy Policy
                    </h4>
                    <div className="mt-5 gap-4">
                      <p className="text-md lg:text-lg text-default-600 font-medium block max-w-full">
                        We may update this Privacy Policy from time to time. The
                        updated version will be indicated by an updated
                        “Revised” date. We encourage you to review this Privacy
                        Policy frequently to stay informed of how we are
                        protecting your information.
                      </p>
                    </div>
                  </div>
                  <Divider className="my-8" />
                  <div className="py-6" id="contact">
                    <h3 className="text-3xl lg:text-6lg tracking-tight inline font-semibold">
                      Contact Us
                    </h3>
                    <div className="mt-5 gap-4">
                      <p className="lg:text-lg text-default-600 mb-8 font-medium block">
                        If you have questions or comments about this Privacy
                        Policy, please contact us at{" "}
                        <a href="mailto:hello@fncryptotx.com">
                          hello@fncryptotx.com
                        </a>{" "}
                        or
                      </p>
                      <Link
                        className={buttonStyles({
                          color: "primary",
                          radius: "md",
                          size: "lg",
                          variant: "shadow",
                        })}
                        href="/contact-us"
                      >
                        Contact Us
                      </Link>
                    </div>
                  </div>
                </ScrollSpy>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PolicySpyCom;
